/**
 * REST clients for the network execution registry.
 *
 *   GET /networks/{id}/executions          → list all task rows for a network
 *   GET /networks/{id}/executions/{taskId} → detail (SFN history + DDB row)
 *
 * Both endpoints already exist (see `backend/api/rest_handlers.py`
 * `get_network_executions_handler` and `get_execution_details_handler`); this
 * module is the canonical typed client. The `BuildTab` workbench page used to
 * inline these calls — Phase 3 lifts them so the agentic room's "Executions"
 * tab can share the same shape.
 *
 * Status vocabulary is **mixed** between Brain (`SUCCESS` / `FAILED`) and
 * Step Functions (`SUCCEEDED` / `FAILED` / `RUNNING` / `TIMED_OUT` / `ABORTED`)
 * plus the bespoke `AWAITING_APPROVAL` and `UNKNOWN` returns. We surface the
 * raw string and let the UI layer normalize.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

/**
 * Row shape returned by `GET /networks/{id}/executions`. Backend currently
 * picks a small subset of the underlying DynamoDB Tasks row; we keep the
 * record open-ended (`Record<string, unknown>` extension) so future fields
 * land for free.
 */
export type NetworkExecutionSummary = Record<string, unknown> & {
  taskId: string;
  prompt?: string;
  status?: string;
  createdAt?: string;
  attempts?: number;
  /**
   * Generative (manifest-engine) provenance. `engine === 'manifest'` marks a run on
   * the SHARED engine Step Function (which executes EVERY generative room), vs a
   * classic per-network SFN. `enginePath` is the full room path this run belongs to
   * (`networkId/poolId[/release]`) — the "alias" the user asked for. Listing itself is
   * already room-scoped server-side (poolExecKey); these are for the badge + detail.
   * Absent on classic per-network-SFN rows.
   */
  engine?: string;
  enginePath?: string;
};

export type NetworkExecutionDetail = Record<string, unknown> & {
  taskId: string;
  networkId?: string;
  status?: string;
  prompt?: string;
  /** Architect rationale (string or pre-formatted JSON). */
  architectExplanation?: string;
  /** Awaiting-approval design payload — same content as `architectExplanation`. */
  design?: string;
  /** Resume token, only set on `AWAITING_APPROVAL`. */
  taskToken?: string;
  /** When ``status === FAILED``, the cause string from SFN. */
  error?: string;
  /** Last state input snapshot when the run failed. */
  lastInput?: Record<string, unknown>;
  /** Final output's ASL (post-run). */
  asl?: Record<string, unknown>;
  /** Final output's React Flow graph data, if visualizer populated it. */
  graphData?: Record<string, unknown>;
};

function authHeaders() {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  } as const;
}

/**
 * List task executions for a network. Server-side paginated + optional
 * date-range filter:
 *
 *   - ``limit``  — page size (default 20, max 100)
 *   - ``cursor`` — opaque base64 cursor returned as ``nextCursor`` from
 *                  the previous page; ``undefined`` for the first page
 *   - ``from`` / ``to`` — ISO-8601 date strings filtering on
 *                  ``createdAt`` (the GSI sort key, so this is a native
 *                  server-side range query, not in-memory filtering)
 *
 * For pool-scoped views, also pass ``poolId`` — the filter is applied
 * client-side because the GSI partitions by ``networkId``, not pool.
 *
 * Returns ``{ executions, nextCursor }``; ``nextCursor === null`` means
 * the last page.
 */
export async function listNetworkExecutions(
  networkId: string,
  opts?: {
    signal?: AbortSignal;
    poolId?: string;
    limit?: number;
    cursor?: string | null;
    from?: string;
    to?: string;
  },
): Promise<{ executions: NetworkExecutionSummary[]; nextCursor: string | null }> {
  if (!networkId || networkId === 'new') return { executions: [], nextCursor: null };
  const params = new URLSearchParams();
  if (typeof opts?.limit === 'number' && opts.limit > 0) {
    params.set('limit', String(opts.limit));
  }
  // ``poolId`` is now a SERVER scope: the backend queries PoolExecutionsIndex so a
  // room's page is server-side, most-recent-first, correctly counted. (Previously it
  // was a client-side filter over a network page — which broke pagination at scale.)
  if (opts?.poolId) params.set('poolId', String(opts.poolId));
  if (opts?.cursor) params.set('cursor', opts.cursor);
  if (opts?.from) params.set('from', opts.from);
  if (opts?.to) params.set('to', opts.to);
  const qs = params.toString();
  const url =
    `${API_BASE_URL.replace(/\/$/, '')}/networks/${encodeURIComponent(networkId)}/executions`
    + (qs ? `?${qs}` : '');
  const res = await fetch(url, {
    headers: authHeaders(),
    signal: opts?.signal,
  });
  if (!res.ok) {
    const t = await res.text().catch(() => '');
    throw new Error(t || `HTTP ${res.status}`);
  }
  const data = (await res.json()) as {
    executions?: NetworkExecutionSummary[];
    nextCursor?: string | null;
  };
  const executions = Array.isArray(data.executions) ? data.executions : [];
  return {
    executions,
    nextCursor: typeof data.nextCursor === 'string' ? data.nextCursor : null,
  };
}

/**
 * Fetch one execution's combined SFN + DDB detail. Tolerates HTTP 404 and
 * 500 by returning ``null``; UI renders an empty/error state rather than
 * surfacing a thrown exception in the drawer.
 */
export async function getNetworkExecution(
  networkId: string,
  taskId: string,
  opts?: { signal?: AbortSignal },
): Promise<NetworkExecutionDetail | null> {
  if (!networkId || !taskId) return null;
  const url = `${API_BASE_URL.replace(/\/$/, '')}/networks/${encodeURIComponent(networkId)}/executions/${encodeURIComponent(taskId)}`;
  const res = await fetch(url, {
    headers: authHeaders(),
    signal: opts?.signal,
  });
  if (!res.ok) return null;
  return (await res.json()) as NetworkExecutionDetail;
}

/** One agent's GENERATED artifact in a build manifest. */
export interface BuildManifestAgent {
  logicalId: string;
  type?: string;
  systemPrompt?: string;
  usage?: Record<string, unknown> | null;
}

/** One GENERATED skill in a build manifest. */
export interface BuildManifestSkill {
  skillId?: string;
  name: string;
  whenToUse?: string;
  instructions?: string;
  selfImproving?: boolean;
  usage?: Record<string, unknown> | null;
}

/** Immutable per-revision build manifest (builds/{taskId}.json). The GENERATED
 *  prompts + skills for one revision — frozen against later user edits. */
export interface BuildManifest {
  taskId: string;
  networkId?: string;
  generatedAt?: string;
  prompt?: string;
  /** Architect (design step) usage — opus/reason tier; its own lane in the box. */
  architectUsage?: Record<string, unknown> | null;
  agents: BuildManifestAgent[];
  skills: BuildManifestSkill[];
}

/**
 * Fetch one revision's immutable build manifest via
 * ``GET /networks/{id}/executions/{taskId}?view=build``. Returns ``null`` on
 * 404/500 (older revisions or networks predating manifests).
 */
export async function getBuildManifest(
  networkId: string,
  taskId: string,
  opts?: { signal?: AbortSignal },
): Promise<BuildManifest | null> {
  if (!networkId || !taskId) return null;
  const url = `${API_BASE_URL.replace(/\/$/, '')}/networks/${encodeURIComponent(networkId)}/executions/${encodeURIComponent(taskId)}?view=build`;
  const res = await fetch(url, { headers: authHeaders(), signal: opts?.signal });
  if (!res.ok) return null;
  const data = await res.json();
  return (data && typeof data === 'object' ? (data.build ?? null) : null) as BuildManifest | null;
}
